/**
 * Courier Comparison — side-by-side comparison of up to 4 PARENT couriers on
 * selected metrics, within one service type (Surface / Air / SDD / NDD).
 * Live data: same CT metrics as the performance tab, grouped to parent courier.
 */
import React, {useState, useMemo, useEffect} from 'react'
import {subDays} from 'date-fns'
import {
  Box, Grid, Typography, Autocomplete, TextField, Chip,
  FormControl, InputLabel, MenuItem, Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined'
import {apiConstants} from '../../../common/constants'
import {useGenericPostRequestQuery} from '../../../redux/commonRTK'
import {getYYYYMMDD} from '../../../helpers/UtilityHelper'
import DashboardDateFilter, {DateRangeCaption} from '../../../common/DashboardDateFilter'
import Loader from '../../../common/Loader'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE} from '../../../styles/foundation/colors'
import {SHADOW} from '../../../styles/foundation/effects'
import {helpTextSx, helpTextTypeSx} from '../../../styles/patterns'
import {COMPARISON_METRICS, SERVICE_TYPE_OPTIONS, getPerformanceColor, resolveMerchantId} from './performanceUtils'

// FE service-type buckets → backend `page` values.
const toBackendPage = (bucket) => {
  if (bucket === 'SAME_CITY_NDD') return 'SC-NDD'
  if (bucket === 'INTERCITY_NDD') return 'IC-NDD'
  return bucket || 'ALL' // ALL / SURFACE / AIR / SDD
}

const statCardSx = {
  bgcolor: WHITE, borderRadius: '10px', boxShadow: SHADOW.card, border: `1px solid ${BORDER}`,
  overflow: 'hidden', transition: 'box-shadow 0.2s', '&:hover': {boxShadow: SHADOW.cardHover},
}
const headerCellSx = {
  fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE['2xs'], color: SUBTLE,
  textTransform: 'uppercase', letterSpacing: '0.03em', py: 1.5, whiteSpace: 'nowrap',
}
const selectSx = {
  fontFamily: FONT, fontSize: SIZE.xs,
  '& .MuiOutlinedInput-root': {borderRadius: '8px', fontSize: SIZE.xs},
  '& .MuiSelect-select': {py: 0.8},
}

export default function CourierComparison() {
  const [serviceType, setServiceType] = useState('ALL')
  const [selectedNames, setSelectedNames] = useState([])
  const [metrics, setMetrics] = useState(COMPARISON_METRICS.slice(0, 4))
  const [dateRange, setDateRange] = useState([{
    endDate: subDays(new Date(), 1), startDate: subDays(new Date(), 30), key: 'selection',
  }])
  const handleDateChange = setDateRange

  // Backend D2C Courier Performance via RTK Query (cached per args). Forward rows (for the chosen
  // service type) carry delivery + TAT metrics; a parallel reverse (ALL) call adds RVP pickup metrics.
  const merchantId = resolveMerchantId()
  const base = {
    merchantId,
    fromDate: getYYYYMMDD(dateRange[0].startDate),
    toDate: getYYYYMMDD(dateRange[0].endDate),
  }
  // No skip — backend resolves the merchant from the JWT when the FE sends none (admin users
  // often have no merchant in context).
  const {data: fwdData, isFetching: fwdFetching} = useGenericPostRequestQuery(
    {url: apiConstants.D2C_COURIER_PERFORMANCE, type: 'POST', data: {...base, page: toBackendPage(serviceType), direction: 'FORWARD'}})
  const {data: revData, isFetching: revFetching} = useGenericPostRequestQuery(
    {url: apiConstants.D2C_COURIER_PERFORMANCE, type: 'POST', data: {...base, page: 'ALL', direction: 'REVERSE'}})
  const loading = fwdFetching || revFetching

  // Forward rows carry delivery + TAT metrics; reverse rows add the RVP pickup metrics, merged by parent name.
  const pool = useMemo(() => {
    const fwd = Array.isArray(fwdData) ? fwdData : (Array.isArray(fwdData?.data) ? fwdData.data : [])
    const rev = Array.isArray(revData) ? revData : (Array.isArray(revData?.data) ? revData.data : [])
    return fwd.map((d) => {
      const r = rev.find((x) => x.courierName === d.courierName) || {}
      return {...d, rvpPickupPct: r.rvpPickupPct || 0, rvpPlusOnePct: r.rvpPlusOnePct || 0, rvpPlusTwoPct: r.rvpPlusTwoPct || 0}
    })
  }, [fwdData, revData])

  // Default to the top 2 couriers whenever the service type or the data set changes.
  useEffect(() => { setSelectedNames(pool.slice(0, 2).map((c) => c.courierName)) }, [serviceType, pool.length]) // eslint-disable-line

  const selected = pool.filter((c) => selectedNames.includes(c.courierName))
  const courierOptions = pool.map((c) => c.courierName)

  return (
    <>
      {loading && <Loader />}

      <DateRangeCaption dateRange={dateRange} sx={{mb: 2}} />

      {/* Selectors */}
      <Box sx={{bgcolor: WHITE, borderRadius: '10px', boxShadow: SHADOW.card, border: `1px solid ${BORDER}`, p: 2.5, mb: 2.5}}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold, color: NAVY, mb: 1}}>
              Service Type
            </Typography>
            <FormControl variant='outlined' size='small' fullWidth>
              <InputLabel sx={{fontFamily: FONT, fontSize: SIZE.xs}}>Service Type</InputLabel>
              <Select value={serviceType} onChange={(e) => setServiceType(e.target.value)} label='Service Type' sx={selectSx}>
                <MenuItem value='ALL' sx={{fontFamily: FONT, fontSize: SIZE.xs}}>All Forward</MenuItem>
                {SERVICE_TYPE_OPTIONS.map((t) => (
                  <MenuItem key={t.value} value={t.value} sx={{fontFamily: FONT, fontSize: SIZE.xs}}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5}>
            <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold, color: NAVY, mb: 1}}>
              Select Couriers (max 4)
            </Typography>
            <Autocomplete
              multiple
              size='small'
              options={courierOptions}
              value={selectedNames}
              onChange={(_, val) => setSelectedNames(val.slice(0, 4))}
              renderInput={(params) => <TextField {...params} placeholder='Add courier...' sx={{fontFamily: FONT}} />}
              renderTags={(val, getTagProps) => val.map((v, i) => (
                <Chip key={v} label={v} size='small' {...getTagProps({index: i})} sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold}} />
              ))}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold, color: NAVY, mb: 1}}>
              Select Metrics
            </Typography>
            <Autocomplete
              multiple
              size='small'
              options={COMPARISON_METRICS}
              getOptionLabel={(o) => o.label}
              value={metrics}
              onChange={(_, val) => setMetrics(val)}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              renderInput={(params) => <TextField {...params} placeholder='Add metric...' sx={{fontFamily: FONT}} />}
              renderTags={(val, getTagProps) => val.map((v, i) => (
                <Chip key={v.value} label={v.label} size='small' {...getTagProps({index: i})} sx={{fontFamily: FONT, fontSize: SIZE['2xs']}} />
              ))}
            />
          </Grid>
        </Grid>
        <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 2}}>
          <DashboardDateFilter dateRange={dateRange} maxDate={subDays(new Date(), 1)} onChange={handleDateChange} showPresets={false} />
        </Box>
      </Box>

      {/* Help text */}
      <Box sx={helpTextSx}>
        <InfoOutlinedIcon sx={{fontSize: 16, color: PRIMARY_BLUE, mt: 0.2}} />
        <Typography sx={helpTextTypeSx}>
          <strong>Comparison:</strong> Pick a service type, then up to 4 parent couriers to compare within it.
          Color coding indicates performance — green (good), amber (acceptable), red (needs improvement).
        </Typography>
      </Box>

      {selected.length < 2 ? (
        <Box sx={{bgcolor: WHITE, borderRadius: '10px', boxShadow: SHADOW.card, border: `1px solid ${BORDER}`, p: 4, textAlign: 'center'}}>
          <CompareArrowsOutlinedIcon sx={{fontSize: 48, color: BORDER, mb: 1}} />
          <Typography sx={{fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: SUBTLE}}>
            {courierOptions.length < 2 ? 'Not enough couriers with data for this service type' : 'Select at least 2 couriers to compare'}
          </Typography>
        </Box>
      ) : (
        <>
          {/* Comparison cards */}
          <Box sx={{display: 'flex', gap: '12px', mb: 2.5, flexWrap: 'wrap'}}>
            {selected.map((c) => (
              <Box key={c.courierName} sx={{...statCardSx, flex: '1 1 200px', minWidth: 180, p: 2.5}}>
                <Typography sx={{fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY, mb: 1.5, textAlign: 'center'}}>
                  {c.courierName}
                </Typography>
                {metrics.map((m) => {
                  const val = c[m.value]
                  const color = getPerformanceColor(val, m.value.includes('tat') ? 'tat' : m.value.includes('cod') ? 'cod' : 'default')
                  return (
                    <Box key={m.value} sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, borderBottom: `1px solid ${BORDER}`}}>
                      <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}}>{m.label.replace(' %', '')}</Typography>
                      <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color, bgcolor: color + '14', px: 0.8, py: 0.15, borderRadius: '4px'}}>
                        {val != null ? `${val}%` : '—'}
                      </Typography>
                    </Box>
                  )
                })}
              </Box>
            ))}
          </Box>

          {/* Comparison table */}
          <Box sx={{bgcolor: WHITE, borderRadius: '10px', boxShadow: SHADOW.card, border: `1px solid ${BORDER}`, overflow: 'hidden'}}>
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow sx={{bgcolor: '#f8f9fc'}}>
                    <TableCell sx={headerCellSx}>Metric</TableCell>
                    {selected.map((c) => (
                      <TableCell key={c.courierName} sx={headerCellSx} align='center'>{c.courierName}</TableCell>
                    ))}
                    <TableCell sx={headerCellSx} align='center'>Best</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {metrics.map((m) => {
                    const values = selected.map((c) => ({name: c.courierName, val: c[m.value] || 0}))
                    const isLowerBetter = m.value === 'rtoPct'
                    const best = isLowerBetter
                      ? values.reduce((a, b) => a.val < b.val ? a : b)
                      : values.reduce((a, b) => a.val > b.val ? a : b)

                    return (
                      <TableRow key={m.value} sx={{'&:hover': {bgcolor: '#fafbfc'}}}>
                        <TableCell sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold, color: NAVY, py: 1.2}}>
                          {m.label}
                        </TableCell>
                        {values.map((v) => {
                          const metricType = m.value.includes('tat') ? 'tat' : m.value.includes('cod') ? 'cod' : m.value.includes('prepaid') ? 'prepaid' : 'default'
                          const color = m.value === 'rtoPct'
                            ? (v.val > 15 ? '#ef4444' : v.val > 10 ? '#f59e0b' : '#10b981')
                            : getPerformanceColor(v.val, metricType)
                          return (
                            <TableCell key={v.name} align='center'>
                              <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color, bgcolor: color + '14', px: 1, py: 0.25, borderRadius: '6px', display: 'inline-block'}}>
                                {v.val.toFixed(1)}%
                              </Typography>
                            </TableCell>
                          )
                        })}
                        <TableCell align='center'>
                          <Chip label={best.name} size='small' sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, bgcolor: '#10b98118', color: '#10b981'}} />
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </>
      )}
    </>
  )
}
